<template>
  <div
    v-if="loginFormActive"
    class="login-form login-subform.vue"
  >
    <div class="inner-form">
      <b-form-group
        :state="usernameState"
        :invalid-feedback="usernameInvalid"
        class="username-group"
      >
        <b-input-group>
          <b-input-group-text slot="prepend">
            <font-awesome-icon :icon="userIcon" />
          </b-input-group-text>
          <b-form-input
            ref="username"
            v-model="username"
            type="text"
            data-cy="login-form-username"
            placeholder="Email"
            autocomplete="email"
            required
            @keyup.enter="loginEnterKey"
            @focus="focusField = 'username'"
          />
        </b-input-group>
      </b-form-group>
      <b-form-group
        :state="passwordState"
        :invalid-feedback="passwordInvalid"
        class="password-group"
      >
        <b-input-group>
          <b-input-group-text slot="prepend">
            <font-awesome-icon :icon="lockIcon" />
          </b-input-group-text>
          <b-form-input
            ref="password"
            v-model="password"
            :type="passwordFieldType"
            data-cy="login-form-password"
            placeholder="Password"
            autocomplete="current-password"
            required
            @focus="focusField = 'password'"
            @keyup.enter="loginEnterKey"
          />
          <b-input-group-text
            slot="append"
            class="show-password"
            @click="togglePasswordVisibility"
          >
            <div
              v-if="passwordHidden"
              class="password-hidden"
              title="Show Password"
            >
              <font-awesome-icon :icon="eyeIcon" />
            </div>
            <div
              v-else
              class="password-shown"
              title="Hide Password"
            >
              <font-awesome-icon :icon="eyeSlashIcon" />
            </div>
          </b-input-group-text>
        </b-input-group>
      </b-form-group>
      <b-button
        :disabled="loginFormValid"
        class="submit"
        data-cy="login-form-button"
        variant="primary"
        type="submit"
        @click="onLoginSubmit"
      >
        Log In
      </b-button>
    </div>
    <div class="login-actions">
      <div
        v-if="!hideForgotPassword"
        data-cy="login-form-recover-password"
        class="login-action"
        @click="changeRoute('/password/request')"
      >
        Forgot Password
      </div>
      <div
        v-if="!hideCreateAccount"
        data-cy="login-form-create-account"
        class="login-action"
        @click="changeRoute(registerAccountLink)"
      >
        Create New Account
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import applogger from '@applogger'
import {
  faLock,
  faUser,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons'
library.add(
  faLock,
  faUser,
  faEye,
  faEyeSlash
)

export default {
  name: 'login-subform',
  props: {
    hideCreateAccount: {
      type: Boolean,
      required: false,
      default: false
    },
    hideForgotPassword: {
      type: Boolean,
      required: false,
      default: false
    },
    registerAccountLink: {
      type: String,
      required: false,
      default: '/signup'
    }
  },
  data() {
    return {
      loginFormActive: true,
      username: '',
      password: '',
      email: '',
      focusField: null,
      passwordHidden: true,
      dataUpdated: {
        username: false,
        password: false
      }
    }
  },
  computed: {
    lockIcon: () => faLock,
    userIcon: () => faUser,
    eyeIcon: () => faEye,
    faEyeSlashIcon: () => faEyeSlash,
    usernameState() {
      return (!this.dataUpdated.username || this.username.length > 0)
    },
    passwordState(val) {
      return (!this.dataUpdated.password || this.password.length > 0)
    },
    usernameInvalid: function() {
      return 'Please enter your username.'
    },
    passwordInvalid: function() {
      return 'Please enter your password.'
    },
    loginFormValid: function() {
      return !(this.username.length > 0 && this.password.length > 0)
    },
    passwordFieldType() {
      if (this.passwordHidden) {
        return 'password'
      }
      return 'text'
    }
  },
  mounted() {
    setTimeout(() => {
      this.$refs.username.$el.focus()
    }, 250)
  },
  methods: {
    loginEnterKey() {
      if (this.username.trim().length > 0 && this.password.trim().length > 0) {
        return this.onLoginSubmit()
      }
      if (this.username.trim().length > 0 && this.focusField === 'username') {
        this.$refs.password.$el.focus()
      }
      if (this.password.trim().length > 0 && this.focusField === 'password') {
        this.$refs.username.$el.focus()
      }
    },
    changeRoute(route) {
      this.$router.push(route)
      this.toggleModal()
    },
    togglePasswordVisibility() {
      this.passwordHidden = !this.passwordHidden
    },
    usernameInput: function() {
      this.dataUpdated.username = true
    },
    passwordInput: function() {
      this.dataUpdated.password = true
    },
    toggleModal: function() {
      this.$emit('toggle')
    },
    onLoginSubmit: function() {
      if (this.username.trim().length > 0 && this.password.trim().length > 0) {
        let instance = this
        instance.$emit('loginInProgress')
        instance.$eventHub.$emit('showProgressLoader')
        instance.$store.dispatch('user/login', { username: this.username, password: this.password, suppressErrorToast: true })
          // On login we want to save the local storage items related to system configuration
          // and reset all modules within the store - this is the primary store login action
          .then(status => {
            instance.$eventHub.$emit('hideProgressLoader')
            if (status) {
              instance.$eventHub.$emit('loginComplete')
              instance.$store.dispatch('user/getPersonReportData', { uid: instance.$store.getters['user/userid'] })
                .then(report => {
                  instance.$eventHub.$emit('eventReport', { event: 'login', user: report })
                })
              instance.$eventHub.$emit('statusMessage', {type: 'success', title: 'Login Successful!', text: 'Your login attempt succeeded.', showClose: false, timeout: 3500})
              applogger.debug('Login Successful')
            }
            else {
              instance.$eventHub.$emit('eventReport', { event: 'error', errorType: 'Login Failed!', errorMessage: 'Invalid Username or Password. Please try again. If the problem persists please contact the System Administrator.', errorInfo: `username: ${this.username}` })
              instance.$eventHub.$emit('statusMessage', {type: 'error', title: 'Login Failed!', text: 'Invalid Username or Password. Please try again. If the problem persists please contact the System Administrator.', showClose: true})
              applogger.debug('Login Failed')
            }
            this.username = ''
            this.password = ''
          })
          .catch((error) => {
            instance.$eventHub.$emit('hideProgressLoader')
            instance.$eventHub.$emit('loginFailed', error)
            instance.$eventHub.$emit('eventReport', { event: 'error', errorType: 'Login Failed!', errorMessage: error.message })
            instance.$eventHub.$emit('statusMessage', {type: 'error', title: 'Login Failed!', text: error.message, showClose: true})
            applogger.debug('Login Failed: ', error)
            throw error
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .login-form {
    .inner-form {
      margin-bottom: 1em;
      .submit {
        width: 100%;
        padding: 1em;
      }
      .form-group {
        border: 1px solid #d5d5d5;
        border-radius: 3px;
        &.username-group {
          border-bottom: none;
          margin-bottom: 0;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
        &.password-group {
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }
        .input-group-text {
          padding: .75rem;
          background-color: #ffffff;
          border: 0;
          color: #c6c6c6;
          &.show-password {
            transition: color 0.3s;
            cursor: pointer;
            &:hover {
              color: #000000;
            }
            .password-shown {
              color: #343434;
            }
          }
          &:focus {
            background-color: #ffffff;
          }
        }
        .form-control {
          border: 0;
          padding: .75rem;
        }
        input:-webkit-autofill {
          box-shadow: inset 0 0 0px 9999px white;
        }
      }
    }
    .login-actions {
      text-align: center;
      .login-action {
        color: var(--main-link-color);
        cursor: pointer;
        &:not(:last-child) {
          margin-bottom: 1em;
          padding-bottom: 1em;
          border-bottom: 1px solid var(--main-border-color);
        }
        &:hover {
          color: var(--main-highlight-color);
        }
      }
    }
  }
</style>
